/**
 * Petit son de trouvaille, synthétisé par le navigateur.
 *
 * Pas de fichier audio : deux notes générées à la volée pèsent zéro octet, ne
 * demandent aucun téléchargement — donc fonctionnent hors-ligne d'emblée — et
 * évitent un asset de plus à gérer.
 *
 * Le son n'est possible qu'après une interaction de l'utilisateur : les
 * navigateurs bloquent l'audio autrement. Ici c'est le cas, il naît d'un clic.
 */

let context: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (context !== null) return context;
  try {
    context = new AudioContext();
    return context;
  } catch {
    // Audio indisponible : le jeu marche très bien sans.
    return null;
  }
}

/** Deux notes ascendantes, courtes et douces. */
export function playFindSound(): void {
  const audio = getContext();
  if (audio === null) return;

  // Le contexte peut être suspendu tant que le jeune n'a rien touché.
  if (audio.state === "suspended") void audio.resume();

  const start = audio.currentTime;
  for (const [index, frequency] of [660, 990].entries()) {
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();

    oscillator.type = "sine";
    oscillator.frequency.value = frequency;

    // Enveloppe douce : une onde brute claquerait dans les oreilles.
    const at = start + index * 0.09;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.18, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, at + 0.22);

    oscillator.connect(gain).connect(audio.destination);
    oscillator.start(at);
    oscillator.stop(at + 0.25);
  }
}

/**
 * Deux notes DESCENDANTES et plus douces : l'essai n'a pas abouti, mais rien
 * n'est perdu. Volontairement discret — ce n'est pas un buzzer de quiz, et le
 * jeune n'a rien fait de mal en se trompant.
 */
export function playMissSound(): void {
  const audio = getContext();
  if (audio === null) return;
  if (audio.state === "suspended") void audio.resume();

  const start = audio.currentTime;
  for (const [index, frequency] of [420, 330].entries()) {
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();

    oscillator.type = "sine";
    oscillator.frequency.value = frequency;

    const at = start + index * 0.08;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.1, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, at + 0.18);

    oscillator.connect(gain).connect(audio.destination);
    oscillator.start(at);
    oscillator.stop(at + 0.2);
  }
}

/** Vibration courte, là où le navigateur le permet (Android surtout). */
export function vibrateFind(): void {
  try {
    navigator.vibrate?.(35);
  } catch {
    // Non supporté : sans conséquence.
  }
}
